import { useEffect, useMemo, useState } from "react";
import { Localized } from "@fluent/react";
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectAllUsers } from "../../../users/usersSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { getStageUiType } from "../documentWorkflowHelpers";
import {
    assignDocumentTeamMemberToStages,
    changeDocumentTeamMemberStages,
    DocumentTeamMemberDto,
    DocumentWorkflowDefinitionDto,
    DocumentWorkflowStageDto,
} from "../documentWorkflowApi";
import { stageTypeChipIcons, stageTypeLabels } from "../DocumentWorkflowConfig";

export type AddTeamMemberDialogUser = {
    id: number
    name: string
    userName: string
    email: string
    position: string
    companyName: string
}

type AddTeamMemberDialogProps = {
    mode: "new" | "edit"
    documentId?: number | string | null
    sections: DocumentWorkflowStageDto[]
    workflowTeamMembers: DocumentTeamMemberDto[]
    initialSelectedUserEmail?: string
    initialSelectedStageIds?: number[]
    onClose: () => void
    onSuccess: (definition: DocumentWorkflowDefinitionDto) => void
}

export const AddTeamMemberDialog = ({
    mode,
    documentId,
    sections,
    workflowTeamMembers,
    initialSelectedUserEmail = "",
    initialSelectedStageIds = [],
    onClose,
    onSuccess,
}: AddTeamMemberDialogProps) => {
    const dispatch = useAppDispatch()
    const allUsers = useAppSelector(selectAllUsers)
    const [selectedUserEmail, setSelectedUserEmail] = useState("")
    const [selectedStageIds, setSelectedStageIds] = useState<number[]>([])
    const [isSaving, setIsSaving] = useState(false)
    const [serverError, setServerError] = useState("")

    const allAvailableUsers = useMemo<AddTeamMemberDialogUser[]>(() => {
        return allUsers.map((user, index) => {
            const parsedId = Number(user.id)
            return {
                id: Number.isFinite(parsedId) ? parsedId : -(index + 1),
                name: `${user.firstname} ${user.surname}`.trim(),
                userName: user.userName,
                email: user.userName,
                position: user.position,
                companyName: user.companyName,
            }
        })
    }, [allUsers])

    const selectableUsers = useMemo<AddTeamMemberDialogUser[]>(() => {
        const workflowUserNames = new Set(
            workflowTeamMembers.map(member => member.userName)
        )

        const filtered = allAvailableUsers.filter(user => {
            if (mode === "edit") {
                return user.userName === selectedUserEmail
            }

            return !workflowUserNames.has(user.userName)
        })

        return filtered.sort((left, right) => left.name.localeCompare(right.name))
    }, [allAvailableUsers, workflowTeamMembers, mode, selectedUserEmail])

    const stageOptions = useMemo(() => {
        return sections.map((stage, index) => {
            const stageType = getStageUiType(stage)
            return {
                id: stage.id,
                icon: stageTypeChipIcons[stageType],
                label:
                    `${index + 1}. ${stage.name}`
                    + ` (${stageTypeLabels[stageType]})`,
            }
        })
    }, [sections])

    const selectedUser = selectableUsers.find(user => user.email === selectedUserEmail) ?? null

    useEffect(() => {
        setIsSaving(false)
        setServerError("")
        setSelectedUserEmail(initialSelectedUserEmail)
        setSelectedStageIds(initialSelectedStageIds)
    }, [initialSelectedStageIds, initialSelectedUserEmail])

    const handleStageToggle = (stageId: number) => {
        setSelectedStageIds(current => current.includes(stageId)
            ? current.filter(id => id !== stageId)
            : [...current, stageId])
    }

    const handleClose = () => {
        setSelectedUserEmail("")
        setSelectedStageIds([])
        setServerError("")
        onClose()
    }

    const getServerErrorMessage = (result: any) => {
        let errorMsg = "error"

        if (result.payload && typeof result.payload === "object") {
            const payload = result.payload as {
                kind?: string
                problem?: { title?: string }
            }

            if (payload.kind === "http") {
                const problem = payload.problem
                if (problem?.title) {
                    errorMsg = problem.title
                }
            }
        }

        return errorMsg
    }

    const assignUserToStages = async (userName: string, stageIds: number[]) => {
        const result = await dispatch(
            assignDocumentTeamMemberToStages({
                documentId: String(documentId),
                dto: {
                    userName,
                    stageIds,
                },
            })
        )

        if (assignDocumentTeamMemberToStages.fulfilled.match(result)) {
            return result.payload as DocumentWorkflowDefinitionDto
        }

        throw result
    }

    const handleConfirmEditMode = async (selectedUserName: string, stageIds: number[]) => {
        const existingMember = workflowTeamMembers.find(
            member => member.userName === selectedUserName
        )

        if (!existingMember) {
            const errorMsg = "error"
            setServerError(errorMsg)
            return null
        }

        const currentStageIds = existingMember.workflowStageIds ?? []
        const addStageIds = stageIds.filter(
            stageId => !currentStageIds.includes(stageId)
        )
        const removeStageIds = currentStageIds.filter(
            stageId => !stageIds.includes(stageId)
        )

        const changeResult = await dispatch(
            changeDocumentTeamMemberStages({
                documentId: String(documentId),
                documentTeamMemberId: String(existingMember.id),
                dto: {
                    addStageIds,
                    removeStageIds,
                },
            })
        )

        if (changeDocumentTeamMemberStages.fulfilled.match(changeResult)) {
            return changeResult.payload as DocumentWorkflowDefinitionDto
        }

        const errorMsg = getServerErrorMessage(changeResult)
        setServerError(errorMsg)
        return null
    }

    const handleConfirmNewMode = async (selectedUserName: string, stageIds: number[]) => {
        if (stageIds.length === 0) {
            return null
        }

        return await assignUserToStages(selectedUserName, stageIds)
    }

    const handleConfirm = async () => {
        if (!selectedUser || selectedStageIds.length === 0 || isSaving || !documentId) {
            return
        }

        setIsSaving(true)
        setServerError("")
        try {
            let definition: DocumentWorkflowDefinitionDto | null = null

            if (mode === "edit") {
                definition = await handleConfirmEditMode(
                    selectedUser.userName,
                    selectedStageIds
                )
            } else {
                definition = await handleConfirmNewMode(
                    selectedUser.userName,
                    selectedStageIds
                )
            }

            if (definition) {
                dispatch(showSuccess("saved"))
                onSuccess(definition)
                setSelectedUserEmail("")
                setSelectedStageIds([])
            }
        } catch (error: any) {
            const errorMsg = getServerErrorMessage(error)
            setServerError(errorMsg)
            dispatch(showError("error"))
        } finally {
            setIsSaving(false)
        }
    }

    return <Dialog open={true} fullWidth maxWidth="sm">
        <DialogTitle>
            {mode === "edit" ? "Edytuj etapy osoby" : "Dodaj do zespolu"}
        </DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <LoadUsers component={<Skeleton variant="rounded" height={40} />}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="User"
                        value={selectedUserEmail}
                        onChange={event => setSelectedUserEmail(event.target.value)}
                        disabled={mode === "edit"}
                    >
                        <MenuItem value="" disabled>
                            Wybierz usera
                        </MenuItem>
                        {selectableUsers.map(user => <MenuItem key={user.userName} value={user.email}>
                            {`${user.name} (${user.position}${user.companyName ? ` • ${user.companyName}` : ""})`}
                        </MenuItem>)}
                    </TextField>
                </LoadUsers>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Etapy</Typography>
                    <Stack spacing={0.5}>
                        {stageOptions.map(stage => <FormControlLabel
                            key={stage.id}
                            control={<Checkbox checked={selectedStageIds.includes(stage.id)} onChange={() => handleStageToggle(stage.id)} />}
                            label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                {stage.icon}
                                <span>{stage.label}</span>
                            </Box>}
                        />)}
                    </Stack>
                </Box>
                {
                    serverError && <Alert sx={{ marginTop: 1, }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </Stack>
        </DialogContent>
        <DialogActions>
            <SaveButton
                loading={isSaving}
                onClick={handleConfirm}
                disabled={!selectedUser || selectedStageIds.length === 0 || isSaving}
            />
            <CancelButton onClick={handleClose} loading={isSaving} />

        </DialogActions>
    </Dialog>
}
